import * as Scheduled from 'features/scheduled-messages'
import { atom } from 'nanostores'
import {
	type Dispatch,
	type FormEventHandler,
	type MutableRefObject,
	type SetStateAction,
	useCallback,
	useEffect,
	useRef,
	useState
} from 'react'

import { api } from '../../api'
import { useCurrentId } from '../../hooks'
import { triggerScrollToBottom } from '../../hooks/useBottomScrolling'
import * as files from '../../utils/files'
import { $reply } from '../MessageActions/MessageActions'

export const enum Mode {
	NewMessage,
	Edit
}

interface EditMessage {
	id: string
	text: string
}

const $editMessage = atom<EditMessage | null>(null)

export interface InnerForm extends HTMLFormElement {
	text: HTMLTextAreaElement
}

// const getText = (form: InnerForm | null) => form?.text.value ?? ''

function resetStores() {
	files.clear()
	$editMessage.set(null)
	Scheduled.$rescheduleInEdit.set(null)
}

function useCheckDisable(validate: () => boolean) {
	const [isDisabled, setIsDisabled] = useState(true)

	const updateUI = useCallback(() => {
		const isValid = validate()
		setIsDisabled(!isValid)
	}, [validate])

	return [isDisabled, updateUI] as const
}

function useOnConversationChange(ref: MutableRefObject<InnerForm | null>) {
	const current = useCurrentId()
	const previous = useRef(current)

	useEffect(() => {
		const isSame = current === previous.current

		if (!isSame) {
			previous.current = current
			ref.current?.reset()
			$reply.set(null)
			Scheduled.$isScheduledShown.set(false)
		}
	}, [current, ref])
}

Scheduled.$rescheduleInEdit.listen((message, prev) => {
	if (message) {
		$editMessage.set({
			id: message.id ?? '',
			text: message.text ?? ''
		})

		return
	}

	if (prev !== null && message === null) {
		return $editMessage.set(null)
	}
})

Scheduled.$isScheduledShown.listen(isActive => {
	if (isActive) {
		$editMessage.set(null)
	}
})

function useOnEditRequest(
	setText: Dispatch<SetStateAction<string>>,
	ref: MutableRefObject<InnerForm | null>
) {
	useEffect(() => {
		function watch(editMessage: null | { text?: null | string }) {
			if (editMessage && ref.current) {
				const area = ref.current.text
				area.focus()

				setText(editMessage.text ?? '')
			} else {
				setText('')
			}
		}

		return $editMessage.listen(watch)
	}, [setText, ref])
}

function useSubmit(text: string) {
	const conversation = useCurrentId()

	const [sendMessage, sendMessageProps] = api.useSendMessageMutation()
	const [updateMessage, updateMessageProps] = api.useEditMessageMutation()
	const [reschedule, rescheduleProps] =
		Scheduled.api.useUpdateScheduledMessageMutation()

	const isLoading =
		sendMessageProps.isLoading ||
		updateMessageProps.isLoading ||
		rescheduleProps.isLoading

	const handleSubmit: FormEventHandler<InnerForm> = async event => {
		event.preventDefault()

		if (isLoading) {
			return
		}

		const form = event.currentTarget

		function onFinish() {
			form.reset()
			$reply.set(null)
		}
		const editReschedule = Scheduled.$rescheduleInEdit.get()

		if (editReschedule) {
			const { data } = await reschedule({
				id: editReschedule.id ?? '',
				text,
				time: editReschedule.time ?? ''
			})

			data && onFinish()

			return
		}

		if (Scheduled.$isScheduledShown.get()) {
			return Scheduled.UI.TimePicker.open(text)
		}

		const edit = $editMessage.get()

		const body = {
			conversation,
			text
		}

		const reply = $reply.get()

		const mutation = edit
			? updateMessage({ ...body, id: edit.id })
			: sendMessage({
					...body,
					attachments: files.getAttachments(),
					reply_to: reply
						? {
								original_message_entity_id: reply.message_id,
								original_send_at: reply.created_at,
								original_sender_entity_id: reply.from?.entityId ?? '',
								original_text: reply.text
							}
						: undefined
				})

		const { data } = await mutation

		if (data !== undefined) {
			onFinish()
				setTimeout(() => {
					triggerScrollToBottom()
				}, 500)
		}
	}

	return {
		conversation,
		handleSubmit,
		isLoading
	}
}

export {
	$editMessage,
	resetStores,
	useCheckDisable,
	useOnConversationChange,
	useOnEditRequest,
	useSubmit
}
